import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';

import { useOnSearch } from 'src/hooks/useOnSearch';
import { usePaginableList } from 'src/hooks/usePaginableList';
import { useAlert } from 'src/services/alert/useAlert';
import {
  FirmUserListRequest,
  useLazyFirmUserListQuery,
} from 'src/services/firm';
import { useSelector } from 'src/services/redux';
import { selectUserFirm, selectUserId, User } from 'src/services/user';
import { useShareVideoMutation } from 'src/services/video';
import { isEmpty } from 'src/utils/common';
import { theme } from '../../services/theme';
import { BoxRow } from '../BoxRow';
import { Checkbox } from '../Checkbox';
import { Dialog } from '../Dialog';
import { EmptyList } from '../EmptyList';
import { SearchIcon } from '../icons';
import { Input } from '../Input';
import { Spacer } from '../Spacer';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  videoId?: number;
}

export const ShareModal: FC<Props> = ({
  isOpen,
  onClose,
  videoId,
}) => {
  const userId = useSelector(selectUserId);
  const userFirm = useSelector(selectUserFirm);

  const { searchStr, onSearchDebounced } = useOnSearch();

  const [fetchFirmUserList, { isFetching, isLoading }] =
    useLazyFirmUserListQuery();

  const { fetchTriggerRef, list: userList } = usePaginableList<
    User,
    FirmUserListRequest
  >({
    extraReqArgs: {
      id: userFirm?.id as number,
      search: searchStr,
    },
    fetchList: fetchFirmUserList,
    limit: 10,
    isFetching: isFetching || isLoading,
  });

  const [selectedUsers, setSelectedUsers] = useState<Record<number, true>>({});

  const onSelectUser = useCallback(
    (userId: number) => () => {
      const wasSelected = selectedUsers[userId];
      const updatedSelectedUsers = { ...selectedUsers };

      if (wasSelected) {
        delete updatedSelectedUsers[userId];
      } else {
        Object.assign(updatedSelectedUsers, { [userId]: true });
      }

      setSelectedUsers(updatedSelectedUsers);
    },
    [selectedUsers],
  );

  const [message, setMessage] = useState<string>();

  const onSetMessage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  }, []);

  const [shareVideo, { isLoading: isShareVideoLoading }] =
    useShareVideoMutation();

  const { onOpenAlertDelayed } = useAlert();

  const onSubmit = useCallback(async () => {
    if (!videoId) {
      return;
    }

    const userIds = Object.keys(selectedUsers).map((i) => Number(i));

    try {
      await shareVideo({ message, userIds, videoId }).unwrap();

      onOpenAlertDelayed({
        message: 'The video has been successfully shared.',
        title: 'Video shared.',
        type: 'success',
      });
    } catch (err: any) {
      console.log('Share failure', err);

      const errMsg =
        err?.data?.message === 'VIDEO_ALREADY_SHARED'
          ? 'Video already shared to selected users.'
          : 'There was an error, please try again.';

      onOpenAlertDelayed({
        message: errMsg,
        title: 'Oops!',
        type: 'error',
      });
    } finally {
      onClose();
    }
  }, [message, onClose, selectedUsers, videoId]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      sx={{
        minHeight: '500px',
        padding: '30px 100px',
        [theme.breakpoints.down('sm')]: {
          padding: '20px 30px',
        },
        [theme.breakpoints.down('mobile')]: {
          padding: '20px 10px',
          margin: '15px',
        },
      }}
      title="Share to"
    >
      <BoxRow sx={{ justifyContent: 'space-between', marginBottom: '10px' }}>
        <Box sx={{ display: 'flex', flex: 2 }}>
          <Input
            InputProps={{
              endAdornment: (
                <SearchIcon stroke="#356482" sx={{ fontSize: '14px' }} />
              ),
            }}
            onChange={onSearchDebounced}
            placeholder="Search by name ..."
            sx={{ width: '100%' }}
          />
        </Box>
      </BoxRow>
      {isEmpty(userList) ? (
        isFetching || isLoading ? (
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <EmptyList text="No users to select" />
        )
      ) : (
        <List
          sx={{
            margin: '0 -20px 10px 0',
            maxHeight: '200px',
            overflow: 'auto',
          }}
        >
          {userList
            .filter((user) => user.id !== userId)
            .map((user) => (
              <Box key={user.id}>
                <ListItem
                  disablePadding
                  sx={{
                    height: '42px',
                    justifyContent: 'space-between',
                    paddingLeft: 0,
                    marginTop: '5px',
                    marginBottom: '5px',
                    paddingRight: '30px',
                    [theme.breakpoints.down('sm')]: {
                      paddingRight: '20px',
                      height: '36px',
                    },
                    [theme.breakpoints.down('mobile')]: {
                      paddingRight: '15px',
                      height: '30px',
                    },
                  }}
                >
                  <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap:'2px'
                  }}>
                    <Typography
                      sx={{
                        color: '#356482',
                        fontSize: '16px',
                        fontWeight: '600',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '14px',
                        },
                        [theme.breakpoints.down('mobile')]: {
                          fontSize: '12px',
                        },
                      }}
                    >
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#356482',
                        fontSize: '12px',
                        fontWeight: '600',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '14px',
                        },
                        [theme.breakpoints.down('mobile')]: {
                          fontSize: '12px',
                        },
                      }}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  <Checkbox
                    onClick={onSelectUser(user.id)}
                    checked={!!selectedUsers[user.id]}
                  />
                </ListItem>
                <Divider sx={{ margin: '0 40px 0 10px' }} />
              </Box>
            ))}
          <ListItem ref={fetchTriggerRef} sx={{ height: '42px' }} />
        </List>
      )}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Input
          InputProps={{ sx: { height: '100px' } }}
          multiline
          onChange={onSetMessage}
          placeholder="Add a note ..."
          rows={4}
          value={message}
        />
        <BoxRow sx={{ marginTop: '20px', width: '100%' }}>
          <Button
            onClick={onClose}
            sx={{
              flex: 1,
              height: '40px',
              [theme.breakpoints.down('mobile')]: {
                height: '30px',
              },
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Spacer width="20px" />
          <LoadingButton
            disabled={isEmpty(selectedUsers)}
            loading={isShareVideoLoading}
            onClick={onSubmit}
            sx={{
              flex: 1,
              height: '40px',
              [theme.breakpoints.down('mobile')]: {
                height: '30px',
              },
            }}
            variant="contained"
          >
            Share
          </LoadingButton>
        </BoxRow>
      </Box>
    </Dialog>
  );
};
